import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import SEO from "../components/SEO";
import SectionReveal from "../components/SectionReveal";
import { publicApi } from "../lib/adminApi";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await publicApi.listBlog({ page_size: 12 });
      if (res.ok) setPosts(res.data.items);
      setLoading(false);
    })();
  }, []);

  return (
    <>
      <SEO
        title="Insights from Ridgehawk — Blog"
        description="Operational insights on logistics, compliance, and same-day delivery from Ridgehawk Logistics in Milton, Ontario."
        path="/blog"
      />

      <section className="relative bg-ridge-charcoal text-white" data-testid="blog-hero">
        <div className="container-pad py-20 md:py-24 lg:py-28">
          <SectionReveal>
            <div className="max-w-3xl">
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-ridge-amber">Insights</span>
              <h1 className="font-display mt-4 text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Insights from Ridgehawk
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
                Notes on logistics, compliance, and operating with precision across the GTA.
              </p>
            </div>
          </SectionReveal>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 bg-ridge-amber" />
      </section>

      <section className="section-pad bg-white" data-testid="blog-list-section">
        <div className="container-pad">
          {loading ? (
            <p className="text-sm text-ridge-muted">Loading posts…</p>
          ) : posts.length === 0 ? (
            <SectionReveal>
              <div className="mx-auto max-w-xl rounded-md border-2 border-dashed border-ridge-border bg-ridge-stone p-10 text-center">
                <span className="overline">Coming soon</span>
                <h2 className="font-display mt-3 text-2xl font-bold text-ridge-charcoal">We're building it.</h2>
                <p className="mt-3 text-sm text-ridge-text">
                  Our first articles are on the way. In the meantime, request a quote and we'll keep you posted.
                </p>
                <Link to="/quote" className="btn-accent mt-6 inline-flex">
                  Get a Quote <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </SectionReveal>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((p, idx) => (
                <SectionReveal key={p.id} delay={idx * 0.05}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="group block h-full overflow-hidden rounded-md border border-ridge-border bg-white transition-all hover:-translate-y-0.5 hover:border-ridge-charcoal/25"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    <div className="aspect-[16/10] w-full overflow-hidden bg-ridge-stone">
                      {p.cover_image_url ? (
                        <img src={p.cover_image_url} alt={p.cover_image_alt || ""} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]" loading="lazy" />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-ridge-charcoal to-ridge-amber-dark" />
                      )}
                    </div>
                    <div className="p-6 md:p-7">
                      <div className="flex flex-wrap items-center gap-3 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ridge-muted">
                        {p.published_at ? (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {(p.published_at || "").slice(0, 10)}
                          </span>
                        ) : null}
                        {p.reading_time_minutes ? (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {p.reading_time_minutes} min
                          </span>
                        ) : null}
                      </div>
                      <h3 className="font-display mt-3 text-xl font-semibold leading-snug text-ridge-charcoal transition-colors group-hover:text-ridge-amber-dark">
                        {p.title}
                      </h3>
                      {p.excerpt ? <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-ridge-text">{p.excerpt}</p> : null}
                      {(p.tags || []).length ? (
                        <div className="mt-5 flex flex-wrap gap-1.5">
                          {p.tags.slice(0, 3).map((t) => (
                            <span key={t} className="rounded bg-ridge-stone px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-ridge-charcoal">{t}</span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </Link>
                </SectionReveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
